// src/pages/FieldInterventions/InterventionDetailResponsive.jsx
// Responsive Field Intervention Detail with signature, attachments, and PDF export

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Close as CloseInterventionIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Draw as SignatureIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Check as CheckIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { fieldInterventionsApi, filesApi } from '../../services/apiClient';
import SignatureCanvas from 'react-signature-canvas';
import * as indexedDB from '../../utils/indexedDB';
import * as tokenEncryption from '../../utils/tokenEncryption';

const InterventionDetailResponsive = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();

  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);

  const signaturePadRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({});
  const [closeData, setCloseData] = useState({
    outcome: '',
    cancellation_reason: ''
  });
  const [newComponent, setNewComponent] = useState('');

  const canEdit = hasPermission('field_interventions.edit');
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  const getTokenFromStorage = async (key) => {
    try {
      if (indexedDB.isAvailable()) {
        const encryptedToken = await indexedDB.getItem(key);
        if (encryptedToken) {
          const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
          if (decryptedToken) return decryptedToken;
        }
      }
      const localToken = localStorage.getItem(key);
      if (localToken) return localToken;
      return null;
    } catch (error) {
      console.error('Error retrieving token:', error);
      return localStorage.getItem(key);
    }
  };

  useEffect(() => {
    loadIntervention();
  }, [id]);

  const loadIntervention = async () => {
    try {
      setLoading(true);
      const response = await fieldInterventionsApi.getById(id);
      setIntervention(response.data.intervention);
      setFormData(response.data.intervention);
    } catch (error) {
      console.error('Error loading intervention:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_loading') : 'Failed to load intervention',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (additionalData = {}) => {
    try {
      setSaving(true);

      const dataToSend = { ...formData, ...additionalData };

      // Auto-update status to "in_progress" if it's pending or undefined (unless explicitly overridden in additionalData)
      if (!additionalData.hasOwnProperty('status') && (!dataToSend.status || dataToSend.status === 'pending')) {
        dataToSend.status = 'in_progress';
      }

      await fieldInterventionsApi.update(id, dataToSend);

      enqueueSnackbar(
        t ? t('FieldInterventions.intervention_updated') : 'Intervention updated successfully',
        { variant: 'success' }
      );
      loadIntervention();
    } catch (error) {
      console.error('Error updating intervention:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_updating') : 'Failed to update intervention',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSignature = () => {
    if (signaturePadRef.current.isEmpty()) {
      enqueueSnackbar('Please provide a signature', { variant: 'warning' });
      return;
    }

    const signatureData = signaturePadRef.current.toDataURL();
    handleFieldChange('technician_signature', signatureData);
    setSignatureDialogOpen(false);
    handleSave({ technician_signature: signatureData });
    enqueueSnackbar('Signature saved', { variant: 'success' });
  };

  const handleClearSignature = () => {
    signaturePadRef.current.clear();
  };

  const handleAddComponent = () => {
    if (!newComponent.trim()) return;

    const currentComponents = formData.components_switched || [];
    const updatedComponents = [...currentComponents, newComponent.trim()];

    handleFieldChange('components_switched', updatedComponents);
    setNewComponent('');
    setComponentDialogOpen(false);
    handleSave({ components_switched: updatedComponents });
    enqueueSnackbar('Component added', { variant: 'success' });
  };

  const handleRemoveComponent = (index) => {
    const currentComponents = formData.components_switched || [];
    const updatedComponents = currentComponents.filter((_, i) => i !== index);

    handleFieldChange('components_switched', updatedComponents);
    handleSave({ components_switched: updatedComponents });
    enqueueSnackbar('Component removed', { variant: 'success' });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'field_intervention_report');
      formData.append('entity_id', id);

      // Link to ticket if available
      if (intervention.ticket_id) {
        formData.append('also_link_to_ticket', intervention.ticket_id);
      }

      await filesApi.upload(formData);

      enqueueSnackbar(`File "${file.name}" uploaded successfully`, { variant: 'success' });
      loadIntervention();
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar(`Failed to upload "${file.name}"`, { variant: 'error' });
    }
  };

  const handleCloseIntervention = async () => {
    if (!closeData.outcome) {
      enqueueSnackbar('Please select an outcome', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);

      await fieldInterventionsApi.close(id, closeData);

      // Note: The API automatically closes the linked ticket on the backend
      enqueueSnackbar(
        t ? t('FieldInterventions.intervention_closed') : 'Intervention closed successfully',
        { variant: 'success' }
      );
      setCloseDialogOpen(false);
      loadIntervention();
    } catch (error) {
      console.error('Error closing intervention:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_closing') : 'Failed to close intervention',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsInvoiced = async (checked) => {
    try {
      setSaving(true);

      await fieldInterventionsApi.markAsInvoiced(id, { invoiced: checked });

      enqueueSnackbar(
        checked
          ? (t ? t('FieldInterventions.intervention_marked_invoiced') : 'Intervention marked as invoiced')
          : (t ? t('FieldInterventions.intervention_unmarked_invoiced') : 'Invoice status updated'),
        { variant: 'success' }
      );
      loadIntervention();
    } catch (error) {
      console.error('Error marking as invoiced:', error);
      const errorMessage = error.response?.data?.error ||
        (t ? t('FieldInterventions.error_marking_invoiced') : 'Failed to mark as invoiced');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      // Reload to revert checkbox state
      loadIntervention();
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const response = await fieldInterventionsApi.getPDF(id);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intervention-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar('PDF downloaded', { variant: 'success' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      enqueueSnackbar('Failed to generate PDF', { variant: 'error' });
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!intervention) {
    return (
      <Container>
        <Box sx={{ py: 4 }}>
          <Alert severity="error">Intervention not found</Alert>
        </Box>
      </Container>
    );
  }

  const isCompleted = intervention.status === 'completed' || intervention.status === 'cancelled';

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Button startIcon={<BackIcon />} onClick={() => navigate('/field-interventions')}>
              {t ? t('FieldInterventions.back_to_interventions') : 'Back to Interventions'}
            </Button>
            {intervention.ticket_id && (
              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={() => navigate(`/tickets?selectedId=${intervention.ticket_id}`)}
              >
                {t ? t('FieldInterventions.view_linked_ticket') : 'View Linked Ticket'}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {!isCompleted && canEdit && (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSave()}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (t ? t('FieldInterventions.save') : 'Save')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={() => setCloseDialogOpen(true)}
                >
                  {t ? t('FieldInterventions.complete_intervention') : 'Complete'}
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={handleGeneratePDF}
            >
              {t ? t('FieldInterventions.download_pdf_report') : 'PDF'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Main Info */}
          <Grid item xs={12} md={8}>
            {/* Intervention Info */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                {t ? t('FieldInterventions.intervention_info') : 'Intervention Information'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t ? t('FieldInterventions.location') : 'Location'}
                  </Typography>
                  <Typography variant="body1">{intervention.location?.friendlyName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t ? t('FieldInterventions.device') : 'Device'}
                  </Typography>
                  <Typography variant="body1">{intervention.device?.friendlyName || 'No device'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t ? t('FieldInterventions.intervention_date') : 'Intervention Date'}
                  </Typography>
                  <Typography variant="body1">{new Date(intervention.intervention_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t ? t('FieldInterventions.status') : 'Status'}
                  </Typography>
                  <Chip
                    label={t ? t(`FieldInterventions.status_${intervention.status || 'pending'}`) : (intervention.status || 'Pending')}
                    color={
                      intervention.status === 'completed' ? 'success' :
                      intervention.status === 'cancelled' ? 'error' :
                      intervention.status === 'in_progress' ? 'warning' :
                      'default'
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Work Performed */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t ? t('FieldInterventions.work_performed') : 'Work Performed'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t ? t('FieldInterventions.what_is_wrong') : 'What is Wrong'}
                    value={formData.what_is_wrong || ''}
                    onChange={(e) => handleFieldChange('what_is_wrong', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t ? t('FieldInterventions.what_we_did') : 'What We Did'}
                    value={formData.what_we_did || ''}
                    onChange={(e) => handleFieldChange('what_we_did', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={t ? t('FieldInterventions.what_tried_before') : 'What Was Tried Before'}
                    value={formData.what_tried_before || ''}
                    onChange={(e) => handleFieldChange('what_tried_before', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={t ? t('FieldInterventions.follow_up_actions') : 'Follow-up Actions'}
                    value={formData.follow_up_actions || ''}
                    onChange={(e) => handleFieldChange('follow_up_actions', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={t ? t('FieldInterventions.notes') : 'Notes'}
                    value={formData.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Components/Parts Used */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t ? t('FieldInterventions.components_replaced') : 'Components Replaced'}
                </Typography>
                {!isCompleted && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setComponentDialogOpen(true)}
                  >
                    {t ? t('FieldInterventions.add_component') : 'Add Component'}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {formData.components_switched && formData.components_switched.length > 0 ? (
                <List dense>
                  {formData.components_switched.map((component, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={component} />
                      {!isCompleted && (
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveComponent(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No components replaced
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Time & Billing */}
          <Grid item xs={12} md={4}>
            {/* Time Tracking */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t ? t('FieldInterventions.time_tracking') : 'Time Tracking'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t ? t('FieldInterventions.technician_name') : 'Technician'}
                    value={formData.technician_name || ''}
                    onChange={(e) => handleFieldChange('technician_name', e.target.value)}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label={t ? t('FieldInterventions.start_time') : 'Start Time'}
                    value={formData.start_time ? new Date(formData.start_time).toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleFieldChange('start_time', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label={t ? t('FieldInterventions.end_time') : 'End Time'}
                    value={formData.end_time ? new Date(formData.end_time).toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleFieldChange('end_time', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isCompleted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.transport_costs_applicable || false}
                        onChange={(e) => handleFieldChange('transport_costs_applicable', e.target.checked)}
                        disabled={isCompleted}
                      />
                    }
                    label={t ? t('FieldInterventions.transport_costs_applicable') : 'Transport Costs'}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Signature */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t ? t('FieldInterventions.technician_signature') : 'Signature'}
                </Typography>
                {!isCompleted && (
                  <Button
                    size="small"
                    startIcon={<SignatureIcon />}
                    onClick={() => setSignatureDialogOpen(true)}
                  >
                    {intervention.technician_signature ? 'Update' : 'Add'}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {intervention.technician_signature ? (
                <Box
                  component="img"
                  src={intervention.technician_signature}
                  alt="Signature"
                  sx={{
                    width: '100%',
                    maxHeight: 150,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No signature added
                </Typography>
              )}
            </Paper>

            {/* Attachments */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t ? t('FieldInterventions.attachments') : 'Attachments'} ({intervention.attachments?.length || 0})
                </Typography>
                {!isCompleted && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => fileInputRef.current.click()}
                  >
                    {t ? t('FieldInterventions.add_file') : 'Add'}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                {intervention.attachments?.map((file) => {
                  const downloadUrl = file.file_id
                    ? `${API_URL}/api/files/${file.file_id}/download?inline=false`
                    : `https://files.caro.care${file.url}`;

                  return (
                    <ListItem key={file.id}>
                      <ListItemIcon>
                        <ImageIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.originalName || file.encrypted_name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={async () => {
                            try {
                              const token = await getTokenFromStorage('auth_token');
                              const response = await fetch(downloadUrl, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (!response.ok) throw new Error('Download failed');

                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = file.originalName || file.encrypted_name || 'download';
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (error) {
                              console.error('Download error:', error);
                              enqueueSnackbar('Failed to download file', { variant: 'error' });
                            }
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                        {!isCompleted && (
                          <IconButton
                            edge="end"
                            onClick={async () => {
                              try {
                                await filesApi.delete(file.file_id);
                                enqueueSnackbar('File deleted successfully', { variant: 'success' });
                                loadIntervention();
                              } catch (error) {
                                console.error('Delete error:', error);
                                enqueueSnackbar('Failed to delete file', { variant: 'error' });
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Outcome (if completed) */}
        {isCompleted && intervention.outcome && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t ? t('FieldInterventions.billing_outcome') : 'Billing & Outcome'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t ? t('FieldInterventions.outcome') : 'Outcome'}
                </Typography>
                <Chip
                  label={t ? t(`FieldInterventions.outcome_${intervention.outcome}`) : intervention.outcome}
                  color={intervention.outcome === 'included_in_contract' ? 'success' : 'warning'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t ? t('FieldInterventions.completed_at') : 'Completed At'}
                </Typography>
                <Typography variant="body1">{intervention.completed_at ? formatDateTime(intervention.completed_at) : '-'}</Typography>
              </Grid>

              {/* Invoiced checkbox - only show for billable interventions */}
              {intervention.outcome === 'not_included' && canEdit && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={intervention.invoiced || false}
                        onChange={(e) => handleMarkAsInvoiced(e.target.checked)}
                        disabled={saving}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">
                          {t ? t('FieldInterventions.mark_as_invoiced') : 'Mark as Invoiced'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t ? t('FieldInterventions.invoiced_description') : 'Check this box when the invoice has been created for this billable intervention'}
                        </Typography>
                      </Box>
                    }
                  />
                  {intervention.invoiced && intervention.invoiced_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                      {t ? t('FieldInterventions.invoiced_at') : 'Invoiced at'}: {formatDateTime(intervention.invoiced_at)}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        multiple
        accept="image/*,application/pdf"
      />

      {/* Signature Dialog */}
      <Dialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Technician Signature</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mt: 1
            }}
          >
            <SignatureCanvas
              ref={signaturePadRef}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearSignature}>Clear</Button>
          <Button onClick={() => setSignatureDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSignature}>
            Save Signature
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Component Dialog */}
      <Dialog
        open={componentDialogOpen}
        onClose={() => setComponentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Component/Part</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Component Name"
            value={newComponent}
            onChange={(e) => setNewComponent(e.target.value)}
            placeholder="e.g., Camera Module, Network Cable, etc."
            autoFocus
            sx={{ mt: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddComponent();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComponentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddComponent} disabled={!newComponent.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Intervention Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Intervention</DialogTitle>
        <DialogContent>
          {intervention.ticket_id && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The linked ticket will be automatically closed.
            </Alert>
          )}
          <TextField
            fullWidth
            select
            label="Billing Outcome"
            value={closeData.outcome}
            onChange={(e) => setCloseData({ ...closeData, outcome: e.target.value })}
            SelectProps={{ native: true }}
            sx={{ mb: 2, mt: 1 }}
          >
            <option value="">Select outcome...</option>
            <option value="included_in_contract">Included in Contract</option>
            <option value="not_included">Billable - Not in Contract</option>
            <option value="cancelled">Cancelled</option>
          </TextField>
          {closeData.outcome === 'cancelled' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Cancellation Reason"
              value={closeData.cancellation_reason}
              onChange={(e) => setCloseData({ ...closeData, cancellation_reason: e.target.value })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCloseIntervention}
            disabled={!closeData.outcome || saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InterventionDetailResponsive;
